import { useQuery } from "@tanstack/react-query"

import { api, type IndexerHealth, type IndexerVerdict } from "@/lib/api"
import { SectionCard } from "@/components/console/SectionCard"
import { StatusDot } from "@/components/console/StatusDot"
import { StatTile } from "@/components/console/StatTile"
import { PageSkeleton, FetchError } from "@/components/console/Fallbacks"
import { formatCount, timeAgo } from "@/lib/format"

// Color never carries the verdict alone — the word is always printed next to the
// swatch, same rule as the dependency dots.
const VERDICT_COLOR: Record<IndexerVerdict, string> = {
  healthy: "var(--status-ok)",
  stale: "var(--status-stale)",
  failing: "var(--viz-failed)",
  unknown: "var(--viz-done)",
  // Push-populated is a working design, not a degradation — it reads as green.
  push: "var(--status-ok)",
  not_applicable: "var(--viz-done)",
}

const VERDICT_LABEL: Record<IndexerVerdict, string> = {
  healthy: "healthy",
  stale: "stale",
  failing: "failing",
  unknown: "unknown",
  push: "push-populated",
  not_applicable: "managed by foundry",
}

function VerdictMark({ verdict }: { verdict: IndexerVerdict }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-hidden
        className="inline-block size-2 shrink-0"
        style={{ background: VERDICT_COLOR[verdict] }}
      />
      <span className="font-mono text-xs text-foreground">{VERDICT_LABEL[verdict]}</span>
    </span>
  )
}

function IndexerRow({ health }: { health: IndexerHealth }) {
  const managed = health.verdict === "not_applicable"
  // A push-populated source has no run to age and no schedule to miss, so the
  // crawl line would be all em-dashes. Its size is the only honest signal.
  const pushed = health.indexer === null && health.document_count !== null
  return (
    <li className="py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="inline-flex items-baseline gap-2">
          <span className="font-mono text-xs text-foreground">{health.source}</span>
          <VerdictMark verdict={health.verdict} />
        </span>
        {managed ? null : pushed ? (
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {formatCount(health.document_count)} documents · written by the app
          </span>
        ) : (
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            ran {timeAgo(health.end_time)}
            {health.last_success_at && health.last_success_at !== health.end_time
              ? ` · last ok ${timeAgo(health.last_success_at)}`
              : ""}
            {" · "}
            {formatCount(health.items_processed)} items
            {health.items_failed ? ` · ${formatCount(health.items_failed)} failed` : ""}
            {health.schedule_interval ? ` · every ${health.schedule_interval}` : ""}
          </span>
        )}
      </div>
      {health.reason && !managed ? (
        <p
          className="mt-1 font-mono text-[11px] leading-relaxed"
          style={{
            color:
              health.verdict === "healthy" || health.verdict === "push"
                ? "var(--muted-foreground)"
                : VERDICT_COLOR[health.verdict],
          }}
        >
          {health.reason}
        </p>
      ) : null}
    </li>
  )
}

export function Health({ token }: { token: string }) {
  const health = useQuery({
    queryKey: ["health", token],
    queryFn: () => api.health(token),
    refetchInterval: 15_000,
  })

  if (health.isPending) return <PageSkeleton />
  if (health.isError) return <FetchError error={health.error} retry={() => health.refetch()} />

  const h = health.data
  return (
    <div className="space-y-3">
      <SectionCard title="dependencies">
        <div className="flex flex-wrap gap-4">
          <StatusDot ok={h.entra.ok} label="entra" detail={h.entra.detail} />
          <StatusDot ok={h.foundry.ok} label="foundry" detail={h.foundry.detail} />
          <StatusDot ok={h.okf_blob.ok} label="okf blob" detail={h.okf_blob.detail} />
          <StatusDot ok={h.graph_mcp.ok} label="graph mcp" detail={h.graph_mcp.detail} />
        </div>
      </SectionCard>
      <SectionCard
        title="indexing pipelines"
        right={
          h.indexers.length ? (
            <span className="inline-flex items-baseline gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                corpus
              </span>
              <VerdictMark verdict={h.corpus_verdict} />
            </span>
          ) : null
        }
      >
        {h.indexers.length ? (
          <ul className="divide-y divide-border">
            {h.indexers.map((indexer) => (
              <IndexerRow key={indexer.source} health={indexer} />
            ))}
          </ul>
        ) : (
          <p className="font-mono text-xs text-muted-foreground">no knowledge sources configured</p>
        )}
      </SectionCard>
      <SectionCard title="curated knowledge">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {Object.entries(h.audience_counts).map(([audience, count]) => (
            <StatTile key={audience} label={audience} value={formatCount(count)} />
          ))}
          <StatTile label="edits, 24h" value={formatCount(h.recent_activity)} />
        </div>
      </SectionCard>
    </div>
  )
}
